// Robust Typing Indicator Hook - Direct RoomMemberEvent.Typing Implementation
// Uses native Matrix typing events for reliable room-specific typing indicators

import { useEffect, useState } from 'react';
import { RoomMemberEvent } from '../constants/matrixEvents';
import { useI18n } from '../context/I18nContext';
import { useMatrixClient } from '../context/MatrixContext';
import { useIgnoredUsers } from './useIgnoredUsers';
import { useDisambiguation } from '../context/DisambiguationContext';

/**
 * Typing Indicator Hook
 * 
 * Uses RoomMemberEvent.Typing events which provide:
 * - Direct room context (no inference needed)
 * - Per-user typing events 
 * - Clean boolean typing state
 * - Native Matrix protocol support
 * 
 * This approach eliminates the need for complex room inference logic.
 */
export const useTypingIndicator = (roomId) => {
  const [typingUsers, setTypingUsers] = useState(new Map()); // userId -> typing state
  const client = useMatrixClient();
  const { t } = useI18n();
  const ignoredUsers = useIgnoredUsers(client);
  const { needsDisambiguation } = useDisambiguation();

  useEffect(() => {
    // If there's no room or client, ensure the state is clear and do nothing.
    if (!roomId || !client) {
      setTypingUsers(new Map());
      return;
    }

    const currentUserId = client.getUserId();
    const room = client.getRoom(roomId);

    // Initialize with currently typing members (important for room switches)
    // When returning to a room, we need to check who is already typing
    // since we won't receive events for users who were already typing
    if (room) {
      const initialTypingUsers = new Map();
      const joinedMembers = room.getJoinedMembers();

      for (const member of joinedMembers) {
        // Check if member is currently typing, not ourselves, and not ignored
        if (member.typing && member.userId !== currentUserId && !ignoredUsers.includes(member.userId)) {
          initialTypingUsers.set(member.userId, {
            userId: member.userId,
            displayName: member.rawDisplayName || member.name || member.userId,
            isTyping: true,
            timestamp: Date.now(),
          });
        }
      }

      setTypingUsers(initialTypingUsers);
    }

    // Listen to individual user typing events using matrixService abstraction
    const handleRoomMemberTyping = (_event, member) => {
      const userId = member.userId;
      const isTyping = member.typing;
      
      // Skip our own typing events and ignored users
      if (userId === currentUserId) return;
      if (ignoredUsers.includes(userId)) return;

      // Update typing state for this specific user
      setTypingUsers(prev => {
        const newMap = new Map(prev);
        
        if (isTyping) {
          // User started typing - add to map with user info
          const room = client.getRoom(roomId);
          const roomMember = room?.getMember(userId);
          
          newMap.set(userId, {
            userId,
            displayName: roomMember?.rawDisplayName || roomMember?.name || userId,
            isTyping: true,
            timestamp: Date.now(),
          });
        } else {
          // User stopped typing - remove from map
          newMap.delete(userId);
        }
        
        return newMap;
      });
    };

    // The logic from matrixService.addTypingListener is now inside the hook.
    const roomMemberTypingListener = (event, member) => {
      if (member.roomId === roomId) handleRoomMemberTyping(event, member);
    };
    client.on(RoomMemberEvent.Typing, roomMemberTypingListener);

    // Cleanup function: runs when roomId changes or the component unmounts.
    return () => {
      client.off(RoomMemberEvent.Typing, roomMemberTypingListener);
      setTypingUsers(new Map()); // Reset the typing users state for the new room.
    };
  }, [roomId, client, ignoredUsers]);

  // Helper to get display name with optional MXID for disambiguation
  const getTypingDisplayName = (user) => {
    const base = user.displayName;
    if (needsDisambiguation(user.userId)) {
      return `${base} (${user.userId})`;
    }
    return base;
  };

  // Convert typing users map to display text
  const formatTypingText = () => {
    const typingUsersList = Array.from(typingUsers.values());

    if (typingUsersList.length === 0) return '';

    if (typingUsersList.length === 1) {
      return t('ComposingIndicator.contactIsTyping', {
        contactName: getTypingDisplayName(typingUsersList[0]),
      });
    }

    if (typingUsersList.length === 2) {
      // Two users: "John & Jane are typing..."
      const nameList = typingUsersList.map(u => getTypingDisplayName(u)).join(' & ');
      return t('ComposingIndicator.areTyping', { nameList });
    }

    // More than 2 users: "John, Jane & 2 others are typing..."
    const firstTwo = typingUsersList.slice(0, 2);
    const remainingCount = typingUsersList.length - 2;
    const othersKey = remainingCount === 1 ? 'ComposingIndicator.other_one' : 'ComposingIndicator.other_plural';
    const othersText = t(othersKey, { numOther: remainingCount });
    const nameList = `${firstTwo.map(u => getTypingDisplayName(u)).join(', ')} & ${othersText}`;

    return t('ComposingIndicator.areTyping', { nameList });
  };

  return {
    typingText: formatTypingText(),
    typingUsers: Array.from(typingUsers.values()),
    typingCount: typingUsers.size,
  };
};

export default useTypingIndicator;
